import { useEffect, useState, useRef } from "react";
import { UploadSimple, ShieldCheck, Eye, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fetchBlobUrl } from "../lib/api";
import VideoPlayer from "./VideoPlayer";
import { Slider } from "./ui/slider";

const METRICS = [
  { key: "hook_strength", label: "Hook strength" },
  { key: "pacing", label: "Pacing / flow" },
  { key: "caption_quality", label: "Caption quality" },
  { key: "visual_quality", label: "Visual quality" },
  { key: "watchability", label: "Watchability" },
];

export default function VideoABHarness({ run, reload }) {
  const [order] = useState(() => (Math.random() < 0.5 ? ["orchestrated", "baseline"] : ["baseline", "orchestrated"]));
  const [urls, setUrls] = useState({});
  const [scores, setScores] = useState({ hook_strength: 3, pacing: 3, caption_quality: 3, visual_quality: 3, watchability: 3 });
  const [winnerSlot, setWinnerSlot] = useState(null); // 0 | 1 | "tie"
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [reveal, setReveal] = useState(false);
  const fileRef = useRef(null);
  const hasBaseline = !!run.baseline_path;

  useEffect(() => {
    (async () => {
      const next = {};
      if (run.output_path) next.orchestrated = await fetchBlobUrl(run.output_path).catch(() => null);
      if (run.baseline_path) next.baseline = await fetchBlobUrl(run.baseline_path).catch(() => null);
      setUrls(next);
    })();
  }, [run.output_path, run.baseline_path]);

  const uploadBaseline = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/video/runs/${run.id}/baseline`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Baseline reel loaded.");
      reload();
    } catch { toast.error("Baseline upload failed."); }
  };

  const submit = async () => {
    if (winnerSlot === null) { toast.error("Pick which reel wins."); return; }
    const winner = winnerSlot === "tie" ? "tie" : order[winnerSlot];
    try {
      await api.post(`/video/runs/${run.id}/scores`, { ...scores, winner, notes });
      setSubmitted(true); setReveal(true);
      toast.success("Scores logged.");
      reload();
    } catch { toast.error("Could not submit scores."); }
  };

  return (
    <div className="mt-14 pt-10 border-t border-border" data-testid="video-ab-harness">
      <div className="flex items-center gap-2 overline mb-2"><ShieldCheck size={14} /> blind a/b harness</div>
      <h2 className="font-mono font-extrabold tracking-tighter text-2xl mb-6">Score orchestrated vs baseline reel</h2>

      {!hasBaseline ? (
        <div className="bg-[#18181B] border border-dashed border-border p-8 flex flex-col items-center" data-testid="video-baseline-upload-zone">
          <UploadSimple size={28} className="text-muted-foreground mb-3" />
          <div className="font-mono text-sm mb-1">upload a baseline reel to compare against</div>
          <div className="overline mb-4" style={{ fontSize: "0.55rem" }}>e.g. CapCut / Opus Clip / manual edit export · mp4 / mov</div>
          <input ref={fileRef} data-testid="video-baseline-input" type="file" accept=".mp4,.mov,.m4v,.webm,video/*" className="hidden" onChange={(e) => uploadBaseline(e.target.files?.[0])} />
          <button data-testid="video-baseline-upload-button" onClick={() => fileRef.current?.click()} className="bg-primary text-black font-mono font-bold px-5 py-2.5 hover:bg-[#FF7733] transition-colors">choose baseline file</button>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border mb-8">
            {[0, 1].map((slot) => {
              const who = order[slot];
              const url = urls[who];
              const label = `CLIP ${slot === 0 ? "A" : "B"}`;
              return (
                <div key={slot} className="bg-[#18181B] p-5" data-testid={`video-ab-clip-${slot === 0 ? "a" : "b"}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono font-bold">{label}</span>
                    {reveal && <span className="overline" style={{ fontSize: "0.55rem", color: who === "orchestrated" ? "#00E559" : "#A1A1AA" }}>{who === "orchestrated" ? "★ orchestrated" : "baseline"}</span>}
                    {!reveal && <span className="overline" style={{ fontSize: "0.55rem" }}>unlabeled</span>}
                  </div>
                  {url ? <VideoPlayer src={url} testid={`video-ab-player-${slot}`} /> : <div className="font-mono text-xs text-muted-foreground">loading…</div>}
                  <button data-testid={`video-ab-pick-${slot}`} disabled={submitted} onClick={() => setWinnerSlot(slot)}
                    className={`mt-4 w-full font-mono text-sm py-2.5 border transition-colors disabled:opacity-50 ${winnerSlot === slot ? "bg-primary text-black border-primary" : "border-border hover:bg-secondary"}`}>
                    {winnerSlot === slot ? <span className="flex items-center justify-center gap-1"><Check weight="bold" size={14} /> {label} wins</span> : `${label} wins`}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex justify-center mb-8">
            <button data-testid="video-ab-pick-tie" disabled={submitted} onClick={() => setWinnerSlot("tie")}
              className={`font-mono text-xs px-4 py-2 border transition-colors disabled:opacity-50 ${winnerSlot === "tie" ? "bg-secondary border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              too close to call · tie
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-x-10 gap-y-6 mb-8">
            {METRICS.map((m) => (
              <div key={m.key}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-sm">{m.label}</span>
                  <span className="font-mono font-bold text-primary" data-testid={`video-score-${m.key}-value`}>{scores[m.key]}<span className="text-muted-foreground text-xs">/5</span></span>
                </div>
                <Slider data-testid={`video-score-${m.key}-slider`} disabled={submitted} min={1} max={5} step={1} value={[scores[m.key]]}
                  onValueChange={(v) => setScores((s) => ({ ...s, [m.key]: v[0] }))} />
                <div className="flex justify-between mt-2 font-mono text-muted-foreground" style={{ fontSize: "0.6rem" }}>
                  {[1, 2, 3, 4, 5].map((num) => <span key={num}>{num}</span>)}
                </div>
              </div>
            ))}
          </div>

          <textarea data-testid="video-ab-notes" disabled={submitted} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="reviewer notes (optional)…"
            className="w-full bg-[#18181B] border border-border p-4 font-mono text-sm mb-6 focus:border-primary outline-none transition-colors disabled:opacity-60" rows={2} />

          {!submitted ? (
            <button data-testid="video-submit-scores-button" onClick={submit}
              className="bg-primary text-black font-mono font-bold px-6 py-3 hover:bg-[#FF7733] hover:-translate-y-[1px] transition-transform">submit scores</button>
          ) : (
            <div className="flex items-center gap-2 font-mono text-sm text-[#00E559]" data-testid="video-scores-submitted"><Eye size={16} /> logged. identities revealed above.</div>
          )}
        </>
      )}

      {run.scores?.length > 0 && (
        <div className="mt-10">
          <div className="overline mb-4">logged scores · {run.scores.length}</div>
          <div className="border border-border overflow-x-auto">
            <table className="w-full font-mono text-xs">
              <thead><tr className="border-b border-border text-muted-foreground">
                {["reviewer", "hook", "pacing", "captions", "visual", "watch", "winner"].map((h) => <th key={h} className="text-left p-3 overline" style={{ fontSize: "0.5rem" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {run.scores.map((s) => (
                  <tr key={s.id} className="border-b border-border last:border-b-0 bg-[#18181B]">
                    <td className="p-3">{s.reviewer}</td>
                    <td className="p-3 text-primary">{s.hook_strength}</td>
                    <td className="p-3 text-primary">{s.pacing}</td>
                    <td className="p-3 text-primary">{s.caption_quality}</td>
                    <td className="p-3 text-primary">{s.visual_quality}</td>
                    <td className="p-3 text-primary">{s.watchability}</td>
                    <td className="p-3" style={{ color: s.winner === "orchestrated" ? "#00E559" : s.winner === "baseline" ? "#A1A1AA" : "#FF5500" }}>{s.winner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
